import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import {
  ArrowUp, ArrowDown, Edit2, Plus, Eye, EyeOff,
  Loader2, Star, GripVertical, MapPin, Calendar, Trash2,
} from "lucide-react";
import { STATUS_LABELS } from "@/lib/constants";
import {
  DndContext, closestCenter,
  PointerSensor, TouchSensor,
  useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const STATUS_STYLE = {
  completed: { bg: "rgba(197,160,89,0.15)", color: "#C5A059",  border: "rgba(197,160,89,0.35)" },
  ongoing:   { bg: "rgba(255,255,255,0.06)", color: "#aaa",    border: "rgba(255,255,255,0.15)" },
  planning:  { bg: "rgba(59,130,246,0.12)",  color: "#60a5fa",  border: "rgba(59,130,246,0.3)"  },
};

const safeTempBase = (list) =>
  Math.max(...list.map((p) => p.sort_order || 0), list.length) * 2 + 100;

// ─── Desktop sortable row ──────────────────────────────────────────────────────

const SortableRow = ({ project, index, totalCount, busy, onSwap, onToggle, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: project.id });

  const isFeatured  = project.sort_order === 1;
  const statusStyle = STATUS_STYLE[project.status] || STATUS_STYLE.ongoing;

  return (
    <tr
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.45 : 1 }}
      className={`border-b border-white/5 transition-colors ${
        isDragging    ? "bg-[#C5A059]/8 shadow-lg"
        : isFeatured  ? "bg-[#C5A059]/4 hover:bg-[#C5A059]/7"
        :                "hover:bg-white/3"
      }`}
    >
      {/* Drag + arrows */}
      <td className="px-3 py-3.5">
        <div className="flex items-center gap-1">
          <button
            {...attributes} {...listeners}
            className="text-[#2a2a2a] hover:text-[#666] cursor-grab active:cursor-grabbing transition-colors touch-none select-none p-1"
            title="Sürükleyerek sırayı değiştir"
          >
            <GripVertical size={15} />
          </button>
          <span className="text-[#555] text-xs tabular-nums w-5 text-center">{project.sort_order}</span>
          <div className="flex flex-col">
            <button onClick={() => onSwap(index, index - 1)} disabled={index === 0 || busy}
              className="p-0.5 text-[#333] hover:text-[#C5A059] disabled:opacity-20 transition-colors">
              <ArrowUp size={11} />
            </button>
            <button onClick={() => onSwap(index, index + 1)} disabled={index === totalCount - 1 || busy}
              className="p-0.5 text-[#333] hover:text-[#C5A059] disabled:opacity-20 transition-colors">
              <ArrowDown size={11} />
            </button>
          </div>
        </div>
      </td>

      {/* Title + thumbnail */}
      <td className="px-4 py-3.5">
        <div className="flex items-center gap-3">
          {project.cover_image ? (
            <img src={project.cover_image} alt="" className="w-12 h-8 object-cover flex-shrink-0 opacity-80"
              onError={(e) => { e.target.style.display = "none"; }} />
          ) : (
            <div className="w-12 h-8 bg-white/5 flex-shrink-0" />
          )}
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[#F5F5F7] text-sm font-light">{project.title}</p>
              {isFeatured && (
                <span className="inline-flex items-center gap-1 text-[#C5A059] text-[9px] tracking-wider border border-[#C5A059]/30 px-1.5 py-0.5">
                  <Star size={7} fill="currentColor" />ÖNE ÇIKAN
                </span>
              )}
            </div>
            <p className="text-[#444] text-[11px] mt-0.5">{project.slug}</p>
          </div>
        </div>
      </td>

      <td className="px-4 py-3.5 hidden lg:table-cell">
        <p className="text-[#555] text-xs">{project.location || "—"}</p>
      </td>
      <td className="px-4 py-3.5 hidden lg:table-cell">
        <p className="text-[#555] text-xs tabular-nums">{project.year || "—"}</p>
      </td>

      <td className="px-4 py-3.5">
        <span className="text-[10px] px-2 py-1 tracking-wide whitespace-nowrap rounded-sm"
          style={{ backgroundColor: statusStyle.bg, color: statusStyle.color, border: `1px solid ${statusStyle.border}` }}>
          {STATUS_LABELS[project.status] || project.status}
        </span>
      </td>

      <td className="px-4 py-3.5 text-center">
        <button onClick={() => onToggle(project)} disabled={busy}
          className="inline-flex items-center gap-1.5 text-xs transition-colors disabled:opacity-50 px-2 py-1"
          style={{ color: project.is_published ? "#22c55e" : "#555" }}>
          {project.is_published ? <Eye size={13} strokeWidth={1.5} /> : <EyeOff size={13} strokeWidth={1.5} />}
          <span className="text-[10px]">{project.is_published ? "Yayında" : "Gizli"}</span>
        </button>
      </td>

      {/* Edit + Delete */}
      <td className="px-3 py-3.5 text-right">
        <div className="flex items-center justify-end gap-1">
          <Link to={`/admin/projeler/${project.id}/duzenle`}
            className="inline-flex items-center gap-1.5 text-xs text-[#555] hover:text-[#C5A059] transition-colors px-2 py-1.5">
            <Edit2 size={11} />Düzenle
          </Link>
          <button onClick={() => onDelete(project)} disabled={busy}
            className="inline-flex items-center gap-1.5 text-xs text-[#3a1a1a] hover:text-red-400 transition-colors px-2 py-1.5 disabled:opacity-40"
            title="Projeyi sil">
            <Trash2 size={11} />
          </button>
        </div>
      </td>
    </tr>
  );
};

// ─── Mobile sortable card ──────────────────────────────────────────────────────

const SortableMobileCard = ({ project, index, totalCount, busy, onSwap, onToggle, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: project.id });

  const isFeatured  = project.sort_order === 1;
  const statusStyle = STATUS_STYLE[project.status] || STATUS_STYLE.ongoing;

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      className={`border-b border-white/5 transition-colors ${
        isDragging   ? "bg-[#C5A059]/8 shadow-xl"
        : isFeatured ? "bg-[#C5A059]/4"
        :               ""
      }`}
    >
      <div className="p-4 flex gap-3">
        {/* Thumbnail */}
        <div className="flex-shrink-0">
          {project.cover_image ? (
            <img src={project.cover_image} alt="" className="w-16 h-12 object-cover opacity-80"
              onError={(e) => { e.target.style.display = "none"; }} />
          ) : (
            <div className="w-16 h-12 bg-white/5 flex items-center justify-center">
              <Star size={14} className="text-[#333]" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="flex-1 min-w-0">
              <p className="text-[#F5F5F7] text-[15px] font-light leading-tight truncate">{project.title}</p>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                {project.location && (
                  <span className="flex items-center gap-1 text-[11px] text-[#555]">
                    <MapPin size={9} />{project.location}
                  </span>
                )}
                {project.year && (
                  <span className="flex items-center gap-1 text-[11px] text-[#555]">
                    <Calendar size={9} />{project.year}
                  </span>
                )}
              </div>
            </div>
            <span className="flex-shrink-0 text-[10px] px-2 py-1 rounded-sm"
              style={{ backgroundColor: statusStyle.bg, color: statusStyle.color, border: `1px solid ${statusStyle.border}` }}>
              {STATUS_LABELS[project.status] || project.status}
            </span>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 mt-2.5 flex-wrap">
            {/* Drag handle */}
            <button
              {...attributes} {...listeners}
              className="p-2 bg-white/5 text-[#444] hover:text-[#888] cursor-grab active:cursor-grabbing touch-none select-none rounded-sm transition-colors"
              title="Sürükleyerek sırala"
            >
              <GripVertical size={14} />
            </button>

            {/* Arrow fallback */}
            <div className="flex gap-1">
              <button onClick={() => onSwap(index, index - 1)} disabled={index === 0 || busy}
                className="p-2 bg-white/5 text-[#555] hover:text-[#C5A059] hover:bg-white/10 disabled:opacity-20 rounded-sm transition-colors">
                <ArrowUp size={13} />
              </button>
              <button onClick={() => onSwap(index, index + 1)} disabled={index === totalCount - 1 || busy}
                className="p-2 bg-white/5 text-[#555] hover:text-[#C5A059] hover:bg-white/10 disabled:opacity-20 rounded-sm transition-colors">
                <ArrowDown size={13} />
              </button>
            </div>

            {/* Publish toggle */}
            <button onClick={() => onToggle(project)} disabled={busy}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-medium transition-colors ${
                project.is_published
                  ? "bg-green-900/30 text-green-400 hover:bg-green-900/50"
                  : "bg-white/5 text-[#666] hover:bg-white/10"
              }`}>
              {project.is_published ? <Eye size={12} /> : <EyeOff size={12} />}
              {project.is_published ? "Yayında" : "Gizli"}
            </button>

            {/* Edit */}
            <Link to={`/admin/projeler/${project.id}/duzenle`}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#C5A059]/10 text-[#C5A059] hover:bg-[#C5A059]/20 rounded-sm text-xs font-medium transition-colors">
              <Edit2 size={12} />Düzenle
            </Link>

            {/* Delete */}
            <button onClick={() => onDelete(project)} disabled={busy}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-900/10 text-red-500/60 hover:bg-red-900/25 hover:text-red-400 border border-red-900/15 rounded-sm text-xs font-medium transition-colors disabled:opacity-40 ml-auto">
              <Trash2 size={12} />Sil
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────

const AdminProjectList = () => {
  const [projects, setProjects] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [busy,     setBusy]     = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor,   { activationConstraint: { delay: 200, tolerance: 8 } }),
  );

  const fetchProjects = useCallback(async () => {
    const { data } = await supabase
      .from("projects")
      .select("id, sort_order, title, slug, location, year, status, is_published, cover_image")
      .order("sort_order", { ascending: true });
    if (data) setProjects(data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const persistNewOrder = async (newOrder) => {
    const base = safeTempBase(newOrder);
    for (let i = 0; i < newOrder.length; i++) {
      await supabase.from("projects").update({ sort_order: base + i + 1 }).eq("id", newOrder[i].id);
    }
    for (let i = 0; i < newOrder.length; i++) {
      await supabase.from("projects").update({ sort_order: i + 1 }).eq("id", newOrder[i].id);
    }
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const fromIdx = projects.findIndex((p) => p.id === active.id);
    const toIdx   = projects.findIndex((p) => p.id === over.id);
    if (fromIdx === -1 || toIdx === -1) return;
    const newOrder = arrayMove(projects, fromIdx, toIdx);
    setProjects(newOrder.map((p, i) => ({ ...p, sort_order: i + 1 })));
    setBusy(true);
    await persistNewOrder(newOrder);
    setBusy(false);
  };

  const swapSortOrder = async (indexA, indexB) => {
    if (busy) return;
    const a = projects[indexA];
    const b = projects[indexB];
    const tmp = safeTempBase(projects);
    setBusy(true);
    await supabase.from("projects").update({ sort_order: tmp }).eq("id", a.id);
    await supabase.from("projects").update({ sort_order: a.sort_order }).eq("id", b.id);
    await supabase.from("projects").update({ sort_order: b.sort_order }).eq("id", a.id);
    setProjects((prev) => {
      const next = [...prev];
      next[indexA] = { ...a, sort_order: b.sort_order };
      next[indexB] = { ...b, sort_order: a.sort_order };
      return next.sort((x, y) => x.sort_order - y.sort_order);
    });
    setBusy(false);
  };

  const togglePublished = async (project) => {
    if (busy) return;
    setBusy(true);
    const next = !project.is_published;
    await supabase.from("projects").update({ is_published: next }).eq("id", project.id);
    setProjects((prev) => prev.map((p) => p.id === project.id ? { ...p, is_published: next } : p));
    setBusy(false);
  };

  const handleDelete = async (project) => {
    const confirmed = window.confirm(
      `"${project.title}" projesini silmek istediğinizden emin misiniz?\n\nTüm proje bilgileri, görseller ve içerikler kalıcı olarak silinecektir. Bu işlem geri alınamaz.`
    );
    if (!confirmed) return;
    setBusy(true);
    await supabase.from("projects").delete().eq("id", project.id);
    setProjects((prev) => prev.filter((p) => p.id !== project.id));
    setBusy(false);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-[#555] text-sm mt-4">
        <Loader2 size={16} className="animate-spin text-[#C5A059]" />
        Projeler yükleniyor...
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[#C5A059] text-[11px] tracking-[0.2em] uppercase mb-1 font-medium">Yönetim</p>
          <h1 className="text-[#F5F5F7] text-2xl font-light">Projeler</h1>
        </div>
        <Link
          to="/admin/projeler/yeni/duzenle"
          className="flex items-center gap-2 bg-[#C5A059] hover:bg-[#D4AF37] text-black px-4 sm:px-5 py-2.5 text-[12px] font-semibold tracking-widest uppercase transition-colors"
        >
          <Plus size={14} />
          <span className="hidden sm:inline">Yeni Proje</span>
          <span className="sm:hidden">Yeni</span>
        </Link>
      </div>

      {busy && (
        <div className="flex items-center gap-2 text-[#C5A059] text-xs mb-3">
          <Loader2 size={13} className="animate-spin" />
          İşlem yapılıyor...
        </div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={projects.map((p) => p.id)} strategy={verticalListSortingStrategy}>

          {/* ── Desktop table (md+) ── */}
          {!isMobile && (
            <div className="bg-[#0d0d0d] border border-white/6 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/6 bg-black/20">
                    <th className="text-left text-[9px] tracking-widest text-[#555] uppercase px-3 py-3 w-24">Sıra</th>
                    <th className="text-left text-[9px] tracking-widest text-[#555] uppercase px-4 py-3">Proje</th>
                    <th className="text-left text-[9px] tracking-widest text-[#555] uppercase px-4 py-3 hidden lg:table-cell">Lokasyon</th>
                    <th className="text-left text-[9px] tracking-widest text-[#555] uppercase px-4 py-3 hidden lg:table-cell w-16">Yıl</th>
                    <th className="text-left text-[9px] tracking-widest text-[#555] uppercase px-4 py-3 w-28">Durum</th>
                    <th className="text-center text-[9px] tracking-widest text-[#555] uppercase px-4 py-3 w-24">Yayın</th>
                    <th className="w-28" />
                  </tr>
                </thead>
                <tbody>
                  {projects.map((project, index) => (
                    <SortableRow
                      key={project.id}
                      project={project}
                      index={index}
                      totalCount={projects.length}
                      busy={busy}
                      onSwap={swapSortOrder}
                      onToggle={togglePublished}
                      onDelete={handleDelete}
                    />
                  ))}
                </tbody>
              </table>
              {projects.length === 0 && (
                <div className="text-center py-16 text-[#444] text-sm">Henüz proje yok.</div>
              )}
            </div>
          )}

          {/* ── Mobile card list ── */}
          {isMobile && (
            <div className="bg-[#0d0d0d] border border-white/6 overflow-hidden">
              {projects.length === 0 ? (
                <div className="text-center py-16 text-[#444] text-sm">Henüz proje yok.</div>
              ) : (
                projects.map((project, index) => (
                  <SortableMobileCard
                    key={project.id}
                    project={project}
                    index={index}
                    totalCount={projects.length}
                    busy={busy}
                    onSwap={swapSortOrder}
                    onToggle={togglePublished}
                    onDelete={handleDelete}
                  />
                ))
              )}
            </div>
          )}

        </SortableContext>
      </DndContext>

      <p className="text-[#333] text-xs mt-3">
        {isMobile ? "≡ simgesine basılı tutarak sürükleyip bırakın." : "Masaüstünde sürükleyip bırakarak sıralayabilirsiniz."}
      </p>
    </div>
  );
};

export default AdminProjectList;
